import type { ComponentProps } from 'react';
import React, { useState } from 'react';
import { useQuery } from '@apollo/client';
import { withQuillContextProvider } from '../../editor/QuillContext';
import type QuillEditor from '../../editor/QuillEditor';
import { gql } from '../../graphql/generated';
import postPath from '../../utils/url/postPath';
import PostDetailPreviewHeader from '../PostDetailPreviewHeader';
import PostSeriesFooter from '../series/PostSeriesFooter';
import * as styles from './ExpandedPostModal.module.scss';
import ExpandedPostModalMagneticWall from './ExpandedPostModalMagneticWall';
import PostModalView from './PostModalView';

const POST_QUERY = gql(/** @complexity 24 */ `
  query ExpandedPostModal_post($id: ID!) {
    post(id: $id) {
      id
      ...PostModalView_post
      ...PostDetailPreviewHeader_post
      ...ExpandedPostModalMagneticWall_post
      ...postPath_post
      series {
        id
        ...PostSeriesFooter_series
      }
    }
    entityAssetToken(entityId: $id, entityType: POST) {
      ...PostModalView_entityAssetToken
    }
  }
`);

type ExpandedPostModalProps = Omit<
  ComponentProps<typeof PostModalView>,
  'onClose' | 'post' | 'entityAssetToken' | 'headerBar'
> & {
  initialId: string;
  close: () => void;
};

const ExpandedPostModal = ({
  initialId,
  close,
  ...props
}: ExpandedPostModalProps) => {
  const [id, setId] = useState(initialId);
  const [quill, setQuill] = useState<QuillEditor | null>(null);

  const { data } = useQuery(POST_QUERY, { variables: { id } });
  const { post, entityAssetToken } = data ?? {};

  return (
    <PostModalView
      {...props}
      onClose={close}
      post={post}
      entityAssetToken={entityAssetToken}
      headerBar={
        post && entityAssetToken && <PostDetailPreviewHeader post={post} />
      }
      editorFooter={
        post?.series && (
          <div className={styles.seriesFooterContainer}>
            <PostSeriesFooter
              postId={post.id}
              series={post.series}
              onBeforeNavigate={(event, postId) => {
                setId(postId);
                event.preventDefault();
              }}
            />
          </div>
        )
      }
      magneticWall={
        quill &&
        post && <ExpandedPostModalMagneticWall quill={quill} post={post} />
      }
      onQuillMount={setQuill}
      newTabTo={post ? postPath(post) : undefined}
    />
  );
};

export default withQuillContextProvider(ExpandedPostModal);
